import { useGetUnreadNotificationsCount } from '@repo/api/notification/useGetUnreadNotificationsCount';
import { ChevronRight } from 'lucide-react';
import { NavLink } from 'react-router';

import type { NavMainItem } from '@/components/features/app-sidebar/AppSidebar';

import { Chip } from '@/components/ui/chip';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { DropdownMenu, DropdownMenuContent, DropdownMenuGroup, DropdownMenuItem, DropdownMenuLabel, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { SidebarGroup, SidebarMenu, SidebarMenuButton, SidebarMenuItem, SidebarMenuSub, SidebarMenuSubButton, SidebarMenuSubItem, useSidebar } from '@/components/ui/sidebar';

interface NavMainProps {
  items: NavMainItem[];
}

export function NavMain(navMainProps: NavMainProps) {
  const { items } = navMainProps;
  return (
    <SidebarGroup>
      <SidebarMenu>
        {items.map((item) => (
          <NavMainItem key={item.title} {...item} />
        ))}
      </SidebarMenu>
    </SidebarGroup>
  );
}

function NavMainItem(item: NavMainItem) {
  const { state, isMobile, setOpenMobile } = useSidebar();

  const handleNavClick = () => {
    if (isMobile) {
      setOpenMobile(false);
    }
  };

  const { data } = useGetUnreadNotificationsCount(item.notificationType, !!item.notificationType);

  if (item.items) {
    if (state === 'expanded' || isMobile) {
      return (
        <Collapsible key={item.title} asChild defaultOpen={item.isActive} className={'group/collapsible'}>
          <SidebarMenuItem>
            <CollapsibleTrigger asChild>
              <SidebarMenuButton tooltip={item.title} onClick={() => item.onClick?.()}>
                {item.icon && <item.icon />}
                <span>{item.title}</span>
                <ChevronRight className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
              </SidebarMenuButton>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <SidebarMenuSub>
                {item.items.map((subItem) => (
                  <SidebarMenuSubItem key={subItem.title}>
                    <SidebarMenuSubButton
                      onClick={() => {
                        item.onClick?.();
                        handleNavClick();
                      }}
                      asChild={!!subItem.url}
                    >
                      {subItem.url ? (
                        <NavLink to={subItem.url}>
                          <span>{subItem.title}</span>
                        </NavLink>
                      ) : (
                        <span>{subItem.title}</span>
                      )}
                    </SidebarMenuSubButton>
                  </SidebarMenuSubItem>
                ))}
              </SidebarMenuSub>
            </CollapsibleContent>
          </SidebarMenuItem>
        </Collapsible>
      );
    }

    return (
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild className="has:aria-[current=page]:bg-accent/25">
            <SidebarMenuButton tooltip={item.title}>
              {item.icon && <item.icon />}
              <span>{item.title}</span>
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent side="right">
            <DropdownMenuLabel>Zarządznie</DropdownMenuLabel>
            <DropdownMenuGroup>
              {item.items.map((subItem) => (
                <DropdownMenuItem
                  key={subItem.title}
                  onClick={() => {
                    subItem.onClick?.();
                    handleNavClick();
                  }}
                  asChild={!!subItem.url}
                >
                  {subItem.url ? (
                    <NavLink to={subItem.url}>
                      <span>{subItem.title}</span>
                    </NavLink>
                  ) : (
                    <span>{subItem.title}</span>
                  )}
                </DropdownMenuItem>
              ))}
            </DropdownMenuGroup>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    );
  }

  return (
    <SidebarMenuItem>
      <Chip isInvisible={!data} variantColor={'brand'} variant={'solid'} isDot={true} size={'sm'} placement={'top-left'} className={'w-full'}>
        <SidebarMenuButton
          tooltip={item.title}
          onClick={() => {
            item.onClick?.();
            handleNavClick();
          }}
          asChild={!!item.url}
        >
          {item.url ? (
            <NavLink to={item.url}>
              {item.icon && <item.icon />}
              <span>{item.title}</span>
            </NavLink>
          ) : (
            <>
              {item.icon && <item.icon />}
              <span>{item.title}</span>
            </>
          )}
        </SidebarMenuButton>
      </Chip>
    </SidebarMenuItem>
  );
}
